/**
 * ConsentCheckbox — Loi 25 (Chunk 9.N.2 v2).
 *
 * Cadrage F2 §3.11 :
 *   - Checkbox **pré-décochée** (cf. `IntakeForm` qui passe `checked={false}` initialement).
 *   - Lien privacy notice → `PRIVACY_NOTICE_URL` exporté par `IntakeChrome`
 *     (single source of truth).
 *   - Persisté côté backend dans `intake_event.payload.consent`.
 *
 * Pattern WIG-aligned :
 *   - `<label>` enveloppe le checkbox + le texte → 1 hit target unique.
 *   - `htmlFor`/`id` cohérent pour les screen readers.
 *   - `aria-required="true"` sur le checkbox.
 *   - `aria-invalid` quand non-coché et que le parent affiche l'erreur.
 *   - Lien privacy avec `target="_blank"` + `rel="noopener noreferrer"`.
 *   - `e.stopPropagation()` sur le click du lien privacy → empêche le toggle
 *     du checkbox parent.
 *
 * Palette mise à jour 9.N.2 v2 : `slate-*` au lieu de `stone-*` pour parité
 * brand board (`docs/branding/kollectivia-brand-system.md`).
 */

import { useId } from "react";
import { useTranslation } from "react-i18next";

import { PRIVACY_NOTICE_URL } from "./IntakeChrome";

export type ConsentCheckboxProps = {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
  readonly showError?: boolean;
};

export function ConsentCheckbox({
  checked,
  onChange,
  disabled = false,
  showError = false,
}: ConsentCheckboxProps): JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-colors hover:bg-slate-50 motion-reduce:transition-none"
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-required="true"
          aria-invalid={showError && !checked ? "true" : undefined}
          aria-describedby={showError && !checked ? errorId : undefined}
          className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-400 text-brand-600 accent-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        />
        <span className="flex-1 text-sm leading-relaxed text-slate-800">
          {t("public_intake.form.consent.label")}{" "}
          <a
            href={PRIVACY_NOTICE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded text-brand-600 underline underline-offset-2 transition-colors hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 motion-reduce:transition-none"
            // Empêche le click sur le lien de toggler le checkbox du label parent.
            onClick={(e) => e.stopPropagation()}
          >
            {t("public_intake.form.consent.privacy_link")}
          </a>
        </span>
      </label>
      {showError && !checked && (
        <p id={errorId} role="alert" className="text-xs font-medium text-red-700">
          {t("public_intake.form.consent.required")}
        </p>
      )}
    </div>
  );
}
